import React, {useState} from 'react';
import {Type, Copy, Loader2, AlertTriangle, ArrowRight} from 'lucide-react';

const ParaphrasingPage = () => {
    const [paraText, setParaText] = useState('');
    const [paraphrased, setParaphrased] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [copied, setCopied] = useState(false);

    const handleParaphrase = async () => {
        if (!paraText.trim()) {
            setError('Please enter text to paraphrase');
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const accessToken = localStorage.getItem('accessToken');

            const payload = {
                text: paraText,
                language: 'urdu'
            };

            const response = await fetch('https://api.digitalaalim.com/paraphrase/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`,
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                throw new Error(`Paraphrasing failed with status: ${response.status}`);
            }

            const data = await response.json();
            setParaphrased(data.paraphrased_text);
        } catch (error) {
            console.error('Error during paraphrasing:', error);
            setError(error.message || 'An error occurred during paraphrasing');
        } finally {
            setIsLoading(false);
        }
    };

    const handleCopyText = () => {
        navigator.clipboard.writeText(paraphrased)
            .then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            })
            .catch(err => {
                console.error('Failed to copy text: ', err);
            });
    };

    return (
        <div className="space-y-8">
            {/* Header */}
            <div>
                <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
                    <Type className="text-blue-600" size={32}/>
                    Urdu Paraphrasing
                </h1>
                <p className="mt-2 text-gray-600">
                    Rephrase Urdu text while preserving the original meaning
                </p>
            </div>

            {/* Main content */}
            <div className="grid md:grid-cols-2 gap-6">
                {/* Source text section */}
                <div className="space-y-4">
                    <h2 className="text-lg font-semibold text-gray-800">Original Text</h2>

                    <textarea
                        value={paraText}
                        onChange={(e) => setParaText(e.target.value)}
                        // placeholder="Enter Urdu text to paraphrase..."
                        className="w-full h-64 p-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none font-urdu"
                        style={{
                            fontFamily: 'Jameel Noori Nastaleeq, Calibri, sans-serif',
                            fontSize: '1.1rem',
                            direction: 'rtl'
                        }}
                    />

                    <button
                        onClick={handleParaphrase}
                        disabled={isLoading || !paraText.trim()}
                        className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-md font-medium transition-colors ${
                            isLoading || !paraText.trim()
                                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                : "bg-blue-600 text-white hover:bg-blue-700"
                        }`}
                    >
                        {isLoading ? (
                            <>
                                <Loader2 className="animate-spin" size={16}/>
                                Paraphrasing...
                            </>
                        ) : (
                            <>
                                <ArrowRight size={16}/>
                                Paraphrase
                            </>
                        )}
                    </button>
                </div>

                {/* Paraphrased text section */}
                <div className="space-y-4">
                    <div className="flex justify-between items-center">
                        <h2 className="text-lg font-semibold text-gray-800">Paraphrased Text</h2>

                        {paraphrased && (
                            <button
                                onClick={handleCopyText}
                                className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                            >
                                <Copy size={14}/>
                                {copied ? 'Copied!' : 'Copy Text'}
                            </button>
                        )}
                    </div>

                    {error && (
                        <div className="flex items-center p-3 bg-red-50 text-red-700 rounded-md">
                            <AlertTriangle size={16} className="mr-2 flex-shrink-0"/>
                            <p className="text-sm">{error}</p>
                        </div>
                    )}

                    <div
                        className={`w-full h-64 p-4 border border-gray-300 rounded-lg overflow-auto ${
                            paraphrased ? 'font-urdu' : 'text-gray-400'
                        }`}
                        style={{
                            fontFamily: paraphrased ? 'Jameel Noori Nastaleeq, Calibri, sans-serif' : 'inherit',
                            fontSize: paraphrased ? '1.1rem' : 'inherit',
                            direction: 'rtl'
                        }}
                    >
                        {paraphrased }
                    </div>

                    {isLoading && (
                        <div className="flex justify-center items-center p-4">
                            <Loader2 className="animate-spin text-blue-600 mr-2" size={20}/>
                            <p className="text-gray-600">Processing your text...</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Information card */}
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                <h3 className="text-md font-medium text-blue-800 mb-2">About Urdu Paraphrasing</h3>
                <p className="text-sm text-blue-700">
                    The paraphrasing tool uses advanced AI to rephrase Urdu text while maintaining its original meaning.
                    This is useful for content creation, avoiding plagiarism, and simplifying complex passages.
                </p>
            </div>
        </div>
    );
};

export default ParaphrasingPage;